import React, { useState, useMemo, useEffect } from "react";
import {
  Alert,
  Button,
  Card,
  Modal,
  Spinner,
  Table,
  Accordion,
} from "react-bootstrap";
import Select from "react-select";
import toast, { Toaster } from "react-hot-toast";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import dayjs from "dayjs";
import { usePayoutPreview } from "../hooks/usePayoutPreview";
import { usePayoutHistory } from "../hooks/usePayoutHistory";
import { useSettlePayout } from "../hooks/useSettlePayout";
import { useMemberships } from "../hooks/useMemberships";
import { DateField } from "../../../components/DateField";

const COP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const HISTORY_COLUMNS = [
  {
    accessorKey: "period_start",
    header: "Desde",
    cell: ({ getValue }) => dayjs(getValue()).format("D MMM YYYY"),
  },
  {
    accessorKey: "period_end",
    header: "Hasta",
    cell: ({ getValue }) => dayjs(getValue()).format("D MMM YYYY"),
  },
  {
    accessorKey: "hours_total",
    header: "Horas",
    cell: ({ getValue }) => `${getValue()} h`,
  },
  {
    accessorKey: "amount_total",
    header: "Monto",
    cell: ({ getValue }) => COP.format(getValue()),
  },
  {
    accessorKey: "paid_at",
    header: "Liquidado",
    cell: ({ getValue }) => dayjs(getValue()).format("D MMM YYYY"),
  },
];

function HonorariosPage() {
  const [teacherOption, setTeacherOption] = useState(null);
  // Fechas como "YYYY-MM-DD" (contrato de DateField, compatible con el backend).
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: memberships, isLoading: membersLoading } =
    useMemberships("TEACHER");

  const teacherOptions = useMemo(
    () =>
      (memberships ?? []).map((m) => ({
        // Honorarios preview resolves teacher by Profile id (payout endpoint).
        value: m.profile ?? m.user?.id ?? m.id,
        label: m.user?.full_name || m.user?.email || `Docente ${m.id}`,
      })),
    [memberships]
  );

  const fromStr = fromDate || null;
  const toStr = toDate || null;

  const {
    data: preview,
    isLoading: previewLoading,
    error: previewError,
  } = usePayoutPreview({
    teacher: teacherOption?.value,
    from: fromStr,
    to: toStr,
  });

  // Picking a teacher auto-applies the period: from the oldest pending class to
  // today. "to" is set immediately; "from" fills in once the preview reports the
  // oldest pending date (so the owner doesn't have to guess the dates).
  const handleTeacherChange = (opt) => {
    setTeacherOption(opt);
    setToDate(dayjs().format("YYYY-MM-DD"));
    setFromDate("");
  };

  useEffect(() => {
    if (!fromDate && preview?.oldest_pending_date) {
      setFromDate(preview.oldest_pending_date);
    }
  }, [preview?.oldest_pending_date, fromDate]);

  const {
    data: history,
    isLoading: historyLoading,
  } = usePayoutHistory({ teacher: teacherOption?.value });

  const settleMutation = useSettlePayout();

  const handleLiquidar = () => {
    settleMutation.mutate(
      {
        teacher: teacherOption.value,
        period_start: fromStr,
        period_end: toStr,
      },
      {
        onSuccess: () => {
          setShowConfirm(false);
          toast.success("Liquidación registrada con éxito.");
        },
        onError: (err) => {
          setShowConfirm(false);
          const msg =
            err?.response?.data?.detail ?? "Error al liquidar. Inténtalo de nuevo.";
          toast.error(msg);
        },
      }
    );
  };

  const table = useReactTable({
    data: history ?? [],
    columns: HISTORY_COLUMNS,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 10 } },
  });

  const canLiquidar =
    !!teacherOption &&
    !!fromStr &&
    !!toStr &&
    (preview?.hours_pending ?? 0) > 0 &&
    !settleMutation.isPending;

  return (
    <>
      <Toaster position="top-right" />
      <div className="container-fluid px-3">
        <div className="py-3">
          <h1 className="h4 mb-0">Honorarios docentes</h1>
          <p className="text-muted small mb-0">
            Preview de horas pendientes y liquidación por periodo
          </p>
        </div>

        {/* Filters — stacked on mobile, row on desktop */}
        <Card className="mb-3">
          <Card.Body>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label className="form-label fw-medium">Docente</label>
                <Select
                  options={teacherOptions}
                  value={teacherOption}
                  onChange={handleTeacherChange}
                  placeholder={membersLoading ? "Cargando…" : "Seleccionar docente"}
                  isLoading={membersLoading}
                  classNamePrefix="react-select"
                />
              </div>
              <div className="col-6 col-md-4">
                <label htmlFor="payout-from" className="form-label fw-medium d-block">
                  Desde
                </label>
                <DateField
                  id="payout-from"
                  value={fromDate}
                  onChange={setFromDate}
                  max={toDate}
                />
              </div>
              <div className="col-6 col-md-4">
                <label htmlFor="payout-to" className="form-label fw-medium d-block">
                  Hasta
                </label>
                <DateField
                  id="payout-to"
                  value={toDate}
                  onChange={setToDate}
                  min={fromDate}
                />
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Preview card — shows as soon as a teacher is picked (dates auto-fill). */}
        {teacherOption && toStr && (
          <Card className="mb-3">
            <Card.Header>
              <strong>Preview de honorarios</strong>
            </Card.Header>
            <Card.Body>
              {previewLoading && (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              )}

              {previewError && (
                <Alert variant="danger" className="mb-0">
                  No se pudo cargar el preview.
                </Alert>
              )}

              {preview && !previewLoading && (
                <>
                  <div className="row g-3 mb-3">
                    <div className="col-6">
                      <p className="text-muted small mb-1">Horas pendientes</p>
                      <h2 className="h3 mb-0" data-testid="hours-pending">
                        {preview.hours_pending ?? 0}
                      </h2>
                      <small className="text-muted">horas</small>
                    </div>
                    <div className="col-6">
                      <p className="text-muted small mb-1">Monto a liquidar</p>
                      <h2 className="h3 mb-0 text-success">
                        {preview.amount_pending != null
                          ? COP.format(preview.amount_pending)
                          : "—"}
                      </h2>
                      {preview.rate_source && (
                        <small className="text-muted">
                          Tarifa: {preview.rate_source}
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Breakdown accordion — collapsed by default on mobile */}
                  {(preview.breakdown ?? []).length > 0 && (
                    <Accordion defaultActiveKey={null}>
                      <Accordion.Item eventKey="0">
                        <Accordion.Header>
                          Ver detalle por sesión ({preview.breakdown.length})
                        </Accordion.Header>
                        <Accordion.Body className="p-0">
                          <div className="table-responsive">
                            <Table size="sm" className="mb-0">
                              <thead className="table-light">
                                <tr>
                                  <th>Fecha</th>
                                  <th className="text-end">Horas</th>
                                  <th className="text-end">Tarifa</th>
                                  <th className="text-end">Subtotal</th>
                                  <th>Fuente</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview.breakdown.map((row, i) => (
                                  <tr key={i}>
                                    <td>
                                      {dayjs(row.session_date).format("D MMM")}
                                    </td>
                                    <td className="text-end">{row.hours} h</td>
                                    <td className="text-end">
                                      {COP.format(row.rate_applied)}
                                    </td>
                                    <td className="text-end">
                                      {COP.format(row.subtotal)}
                                    </td>
                                    <td className="text-muted small">
                                      {row.rate_source}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Accordion.Body>
                      </Accordion.Item>
                    </Accordion>
                  )}

                  <div className="mt-3">
                    <Button
                      variant="primary"
                      style={{ minHeight: 44 }}
                      disabled={!canLiquidar}
                      onClick={() => setShowConfirm(true)}
                    >
                      Liquidar
                    </Button>
                    {(preview.hours_pending ?? 0) === 0 && (
                      <span className="ms-3 text-muted small">
                        Sin horas pendientes en este periodo.
                      </span>
                    )}
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        )}

        {/* History table */}
        {teacherOption && (
          <Card>
            <Card.Header>
              <strong>Histórico de liquidaciones</strong>
            </Card.Header>

            {historyLoading && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            )}

            {!historyLoading && (history ?? []).length === 0 && (
              <Card.Body>
                <p className="text-muted mb-0">
                  No hay liquidaciones registradas para este docente.
                </p>
              </Card.Body>
            )}

            {!historyLoading && (history ?? []).length > 0 && (
              <>
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      {table.getHeaderGroups().map((hg) => (
                        <tr key={hg.id}>
                          {hg.headers.map((h) => (
                            <th key={h.id}>
                              {flexRender(
                                h.column.columnDef.header,
                                h.getContext()
                              )}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map((row) => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                {table.getPageCount() > 1 && (
                  <Card.Footer className="d-flex justify-content-between align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ minHeight: 44 }}
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      Anterior
                    </Button>
                    <span className="text-muted small">
                      Página {table.getState().pagination.pageIndex + 1} de{" "}
                      {table.getPageCount()}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      style={{ minHeight: 44 }}
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      Siguiente
                    </Button>
                  </Card.Footer>
                )}
              </>
            )}
          </Card>
        )}
      </div>

      {/* Confirmation modal */}
      <Modal
        show={showConfirm}
        onHide={() => !settleMutation.isPending && setShowConfirm(false)}
        centered
      >
        <Modal.Header closeButton={!settleMutation.isPending}>
          <Modal.Title>Confirmar liquidación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            ¿Liquidar{" "}
            <strong>
              {preview?.amount_pending != null
                ? COP.format(preview.amount_pending)
                : "—"}
            </strong>{" "}
            para <strong>{teacherOption?.label}</strong>?
          </p>
          <p className="text-muted small mb-0">
            Periodo: {fromStr} al {toStr} ·{" "}
            {preview?.hours_pending ?? 0} horas
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            style={{ minHeight: 44 }}
            onClick={() => setShowConfirm(false)}
            disabled={settleMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            style={{ minHeight: 44 }}
            onClick={handleLiquidar}
            disabled={settleMutation.isPending}
            data-testid="confirm-liquidar"
          >
            {settleMutation.isPending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Confirmar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export { HonorariosPage };
